import { DocumentRecordType, DocumentStatus, type DocumentRead } from "@/types";

/** Which surface the document show page renders for a status. */
export type DocumentShowLayout = "review" | "multi-client-review" | "waiting" | "tabs";

/**
 * Statuses the backend will move on its own BEFORE review. The page has
 * nothing to show in Result / Mapping for these, so it shows a waiting panel
 * instead (#7553). After approval (APPROVED, LOADING, LOADED) the tabs stay,
 * because a load is what is coming and Result is where it lands.
 */
export const PRE_REVIEW_IN_FLIGHT_STATUSES: readonly DocumentStatus[] = [
  DocumentStatus.UPLOADED,
  DocumentStatus.PROCESSING,
  DocumentStatus.REPROCESS,
];

export const resolveDocumentShowLayout = ({
  status,
  isStorageOnly,
  isMultiClient,
}: {
  status: DocumentStatus | undefined;
  isStorageOnly: boolean;
  isMultiClient: boolean;
}): DocumentShowLayout => {
  if (status === DocumentStatus.REVIEW_REQUIRED) return isMultiClient ? "multi-client-review" : "review";
  if (!isStorageOnly && status && PRE_REVIEW_IN_FLIGHT_STATUSES.includes(status)) return "waiting";
  return "tabs";
};

/**
 * The post-load Result view (#7550: read-only review header, claimants table
 * with Outcome and Paid in file) applies to single-client claims documents.
 * Reimbursement and policy loads keep the per-schema cards; multi-client (BOB)
 * documents keep MultiClientReviewPanel's post-load state.
 */
export const usesLoadResultView = (documentRecord: DocumentRead | null | undefined): boolean =>
  !!documentRecord &&
  documentRecord.record_type === DocumentRecordType.STOPLOSS_CLAIMS &&
  !documentRecord.object_metadata?.is_multi_client &&
  documentRecord.object_metadata?.storage_only !== true;

/**
 * Whether the Result tab actually renders the post-load view right now: a
 * claims document (usesLoadResultView) with a load to show. The details drawer
 * is retired only then — its facts moved to the result view's context bar. A
 * FAILED, REJECTED, APPROVED or LOADING claims document with no load yet keeps
 * the drawer (#7553 AC6; !4974 thread 31128).
 */
export const showsLoadResultView = (documentRecord: DocumentRead | null | undefined, hasLatestLoad: boolean): boolean =>
  usesLoadResultView(documentRecord) && hasLatestLoad;

/**
 * Whether the show page renders the details drawer: always for documents the
 * result view never covers; for a claims document only once the loads have
 * resolved and there is none to show. Deciding before the loads resolve showed
 * the drawer for a moment and then dropped it (!4974 thread 31216).
 */
export const keepsDetailsDrawer = (
  documentRecord: DocumentRead | null | undefined,
  loads: { pending: boolean; hasLatestLoad: boolean }
): boolean => !usesLoadResultView(documentRecord) || (!loads.pending && !loads.hasLatestLoad);
